import React from "react";
import { ComboBox as BaseComboBox, MenuHeader, MenuItem, MenuSeparator } from "@skbkontur/react-ui";
import { relativeDef } from "@skbkontur/staff-common";

import { Icon } from "../../../../Controls/Icons/Icon";
import { useAsyncEffect } from "../../../../../../Common/UseAsyncEffect";
import { emptyRef } from "../../../../Controls/TypingUtils";
import { useMetrics } from "../../../../Controls/Internals/Metrics/MetricsProviderContext";
import {
    RELATIVES_COMBOBOX_ADD,
    RELATIVES_COMBOBOX_LOADED,
    RELATIVES_COMBOBOX_OPEN,
    RELATIVES_COMBOBOX_SELECTED,
    RELATIVES_COMBOBOX_UNEXPECTED_INPUT,
} from "../../../../Controls/metricsAction";
import { Text } from "../../../../Controls/ValueViewers/Text/Text";
import { ReadonlyValueWrapper } from "../../../../Controls/ValueEditors/ComboBox/ComboBox.styles";
import { isNotNullOrUndefined } from "../../../../../../Common/TypingUtils";
import { createAbsolute } from "../../../../../../Common/ModelPath/ModelPath";

export interface RelativesComboBoxProps {
    relatives: relativeDef[];
    selectedRelative?: relativeDef | null;
    employeeId?: string;
    selectedLastName?: string;
    selectedFirstName?: string;
    selectedMiddleName?: string;
    isReadOnly?: boolean;
    getRelatives: () => Promise<relativeDef[]>;
    onAdd: (query: string) => void;
    onSelect: (value: relativeDef) => void;
}

export const RelativesComboBox = (props: RelativesComboBoxProps) => {
    const track = useMetrics();
    const inputValue = React.useRef("");
    const comboboxRef = React.useRef<BaseComboBox>(emptyRef);

    const getSelectedRelative = (): relativeDef | null | undefined => {
        if (props.selectedLastName || props.selectedFirstName || props.selectedMiddleName) {
            return {
                id: "",
                employeeId: props.employeeId ?? "",
                employerId: "",
                meta: {
                    version: 1,
                },
                name: {
                    firstName: props.selectedFirstName ?? "",
                    lastName: props.selectedLastName ?? "",
                    middleName: props.selectedMiddleName ?? "",
                },
            };
        }

        return null;
    };

    const selectedRelative = getSelectedRelative();

    React.useEffect(() => {
        if (comboboxRef.current && !selectedRelative) {
            comboboxRef.current.reset();
        }
    }, [selectedRelative]);

    useAsyncEffect(async () => {
        await props.getRelatives();
    }, [props.employeeId]);

    const renderAddButton = (itemsCount: number): JSX.Element[] => [
        <MenuHeader key={"header"}>
            {itemsCount > 0 ? "Если в списке нет нужного — добавьте вручную" : "Нет в справочнике. Добавьте вручную"}
        </MenuHeader>,
        <MenuSeparator key={"separator"} />,
        <MenuItem
            key={"item"}
            icon={<Icon name="ToolPencilLineIcon" />}
            onClick={() => {
                props.onAdd(inputValue.current);
                track(RELATIVES_COMBOBOX_ADD, { employeeId: props.employeeId });
            }}>
            Добавить вручную
        </MenuItem>,
    ];

    const getItems = async (query: string): Promise<relativeDef[]> => {
        track(RELATIVES_COMBOBOX_OPEN, { employeeId: props.employeeId });
        inputValue.current = query;
        const relatives = await props.getRelatives();
        const filteredItems: relativeDef[] = relatives.filter((relative: relativeDef) =>
            renderValue(relative).toLowerCase().includes(query.toLowerCase())
        );

        track(RELATIVES_COMBOBOX_LOADED, { relativesCount: relatives?.length, employeeId: props.employeeId });

        // @ts-ignore
        return [].concat(filteredItems).concat(renderAddButton(filteredItems.length));
    };

    const itemToValue = (relative: relativeDef): string => relative.id;

    const renderValue = (relative: relativeDef): string =>
        `${relative.name.lastName ?? ""} ${relative.name.firstName ?? ""} ${relative.name.middleName ?? ""}`;

    const handleValueChange = (value: relativeDef): void => {
        props.onSelect(value);
        track(RELATIVES_COMBOBOX_SELECTED, { employeeId: props.employeeId, relativeId: value.id });
    };

    const handleUnexpectedInput = () => {
        comboboxRef.current?.reset();
        track(RELATIVES_COMBOBOX_UNEXPECTED_INPUT, { employeeId: props.employeeId });
    };

    if (props.isReadOnly) {
        const selectedValue = getSelectedRelative();
        return (
            <ReadonlyValueWrapper>
                {isNotNullOrUndefined(selectedValue) && (
                    <Text bindingPath={createAbsolute("")} value={renderValue(selectedValue)} />
                )}
            </ReadonlyValueWrapper>
        );
    }

    return (
        <BaseComboBox
            // @ts-ignore
            ref={comboboxRef}
            placeholder={"Начните вводить или выберите из списка"}
            width={"325px"}
            value={getSelectedRelative()}
            getItems={getItems}
            itemToValue={itemToValue}
            renderValue={renderValue}
            renderItem={renderValue}
            valueToString={renderValue}
            onValueChange={handleValueChange}
            onUnexpectedInput={handleUnexpectedInput}
        />
    );
};
